import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const pretty = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("en-GB");
};

const num = (value) =>
  typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value || "—";

export default function DataHealth() {
  const [state, setState] = useState(null);
  const [changes, setChanges] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    Promise.all([api.get("/sources"), api.get("/admin/data-changes?status=pending")])
      .then(([s, c]) => {
        setState(s.data);
        setChanges(c.data);
      })
      .catch((e) => toast.error(errText(e)));
  }, []);

  useEffect(load, [load]);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/admin/data-changes/${id}/${action}`);
      toast.success(action === "approve" ? "Applied and live" : "Rejected");
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  const refresh = async (job) => {
    setBusy(job);
    try {
      await api.post(`/admin/data-feeds/refresh/${job}`);
      toast.success("Refresh started. Reload in a few seconds.");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-data-health">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl uppercase tracking-tight text-white">Data health</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Every declared source, when it was last read, and anything a feed wants to change.
          </p>
        </div>
        <div className="flex gap-2">
          {[["fx", "Refresh FX"], ["world-bank", "Refresh World Bank"]].map(([job, label]) => (
            <button
              key={job}
              type="button"
              onClick={() => refresh(job)}
              disabled={busy === job}
              data-testid={`data-refresh-${job}`}
              className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white hover:border-ochre hover:text-ochre disabled:opacity-50"
            >
              <RefreshCw size={12} /> {label}
            </button>
          ))}
        </div>
      </div>

      {state ? (
        <div className="grid gap-px bg-zinc-900 sm:grid-cols-2 lg:grid-cols-4" data-testid="data-health-summary">
          {[
            ["Declared sources", state.rows.length],
            ["Failing feeds", state.failing.length],
            ["Stale feeds", state.stale.length],
            ["Pending changes", changes.length],
          ].map(([label, value]) => (
            <div key={label} className="bg-[#0b0b0b] p-5">
              <p className="text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">{label}</p>
              <p className="mono mt-2 text-xl font-bold text-ochre">{value}</p>
            </div>
          ))}
        </div>
      ) : null}

      <section>
        <h2 className="font-display text-lg uppercase tracking-tight text-ochre">Waiting for your approval</h2>
        {changes.length === 0 ? (
          <p className="mt-4 border border-zinc-900 bg-[#0b0b0b] p-5 text-sm text-zinc-500" data-testid="data-changes-empty">
            Nothing pending. Costs, fees and valuations only change when you approve them.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {changes.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-4 border border-zinc-900 bg-[#0b0b0b] p-5"
                data-testid={`data-change-${c.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-white">{c.label}</p>
                  <p className="mono mt-1 text-xs text-zinc-400">
                    {num(c.current)} → <span className="text-ochre">{num(c.proposed)}</span> · {c.publisher}
                  </p>
                  {c.note ? <p className="mt-2 text-xs text-zinc-500">{c.note}</p> : null}
                  {c.url ? (
                    <a href={c.url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-ochre underline">
                      Read the source
                    </a>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => act(c.id, "approve")}
                    disabled={busy === c.id + "approve"}
                    data-testid={`data-change-approve-${c.id}`}
                    className="inline-flex items-center gap-2 bg-maasai px-4 py-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white disabled:opacity-50"
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => act(c.id, "reject")}
                    disabled={busy === c.id + "reject"}
                    data-testid={`data-change-reject-${c.id}`}
                    className="inline-flex items-center gap-2 border border-zinc-700 px-4 py-2 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-zinc-300 disabled:opacity-50"
                  >
                    <X size={12} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {state ? (
        <section>
          <h2 className="font-display text-lg uppercase tracking-tight text-ochre">Every declared source</h2>
          <div className="mt-4 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[820px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-900 bg-[#0b0b0b]">
                  {["Figure", "Kind", "Reading", "As at", "Last read", "State"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-900" data-testid={`data-feed-${r.id}`}>
                    <td className="px-4 py-3 text-sm text-white">{r.label}</td>
                    <td className="px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">{r.kind}</td>
                    <td className="mono px-4 py-3 text-xs text-ochre">{num(r.value)}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-400">{r.as_of || "—"}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-500">{pretty(r.fetched_at)}</td>
                    <td className="px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em]">
                      {!r.ok ? (
                        <span className="flex items-center gap-1 text-maasai">
                          <AlertTriangle size={11} /> failing
                        </span>
                      ) : r.stale ? (
                        <span className="text-maasai">stale</span>
                      ) : r.automated ? (
                        <span className="text-ochre">automated</span>
                      ) : (
                        <span className="text-zinc-500">{r.method}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
